/**
 * Constants for UDE Filter components
 */

import { HEALTH_STATUS, DNS_RESPONSE_CODES, HTTP_STATUS_CODES } from 'app/views/ude/dataviews/util/unitFormatter';

// Check if an operator is a field comparison operator
export function isFieldComparisonOperator(operator: string): boolean {
  return operator === 'field_equals' || operator === 'field_not_equal';
}

// Metric filter operators - numeric comparison only
export const METRIC_OPERATORS = [
  { value: '=', label: 'equals' },
  { value: '<>', label: 'does not equal' },
  { value: '>', label: 'greater than' },
  { value: '<', label: 'less than' }
] as const;

/**
 * Display labels for filter operators (used in filter display/summary)
 * Maps operator values to compact display symbols
 */
export const OPERATOR_DISPLAY_LABELS: Record<string, string> = {
  '=': '=',
  '<>': '≠',
  ILIKE: '~',
  'NOT ILIKE': '!~',
  '>': '>',
  '<': '<',
  '~': '~',
  '!~': '!~',
  '~*': '~*',
  '!~*': '!~*'
};

/**
 * Health status filter options
 * For metrics with Unit: 'health'
 */
export const HEALTH_FILTER_OPTIONS = Object.entries(HEALTH_STATUS).map(([code, info]) => ({
  value: code,
  label: info.label
}));

/**
 * DNS response code filter options
 * For metrics with Unit: 'dns_code'
 */
export const DNS_CODE_FILTER_OPTIONS = Object.entries(DNS_RESPONSE_CODES).map(([code, info]) => ({
  value: code,
  label: `${info.label} (${code})`
}));

/**
 * HTTP status code category ranges
 * For metrics with Unit: 'http_status_code'
 */
const HTTP_STATUS_CATEGORIES = [
  { value: '1xx', label: '1xx Informational', min: 100, max: 199 },
  { value: '2xx', label: '2xx Success', min: 200, max: 299 },
  { value: '3xx', label: '3xx Redirection', min: 300, max: 399 },
  { value: '4xx', label: '4xx Client Error', min: 400, max: 499 },
  { value: '5xx', label: '5xx Server Error', min: 500, max: 599 }
] as const;

/**
 * Individual HTTP status code filter options (grouped by category)
 * For more granular filtering
 */
const HTTP_STATUS_FILTER_OPTIONS = Object.entries(HTTP_STATUS_CODES).map(([code, info]) => ({
  value: code,
  label: `${code} ${info.label}`,
  category: info.category
}));

/**
 * Combined HTTP filter options: categories first, then individual codes
 */
export const HTTP_COMBINED_FILTER_OPTIONS = [
  // Category ranges
  ...HTTP_STATUS_CATEGORIES.map((cat) => ({
    value: cat.value,
    label: cat.label,
    isCategory: true
  })),
  // Divider would be handled by the Select component
  // Individual codes
  ...HTTP_STATUS_FILTER_OPTIONS.map((opt) => ({
    ...opt,
    isCategory: false
  }))
];
